import { useState, useMemo, useCallback, useEffect } from 'react'
import SwitchLanguage from '@app/components/Switch'
import { LANGS, TutorialStep } from '@app/constants'
import { useTypedDispatch, useTypedSelector } from '@app/store'
import { changeLanguage } from '@app/store/actions'
import { useLocalization } from '@app/hooks'
import TutorialQuestion from './tutorial_question'
import { questionMark } from '@app/assets/images'
import VideoTutorial from './video_tutorial'
import Timer from './timer'

const TOTAL_STEPS = 3
type Lang = typeof LANGS[keyof typeof LANGS]

const VitalsTutorial: React.FC = () => {
  const { translate } = useLocalization()
  const { language } = useTypedSelector(state => state.lang)
  const dispatch = useTypedDispatch()

  const [currentStep, setCurrentStep] = useState<TutorialStep>(TutorialStep.QUESTION)
  const [selectedLanguage, setSelectedLanguage] = useState<Lang>(language)
  const isRussian = selectedLanguage === LANGS.RUSSIAN

  const stepIndex = useMemo(() => {
    switch (currentStep) {
      case TutorialStep.QUESTION:
        return 0
      case TutorialStep.VIDEO:
        return 1
      case TutorialStep.TIMER:
        return 2
      default:
        return 0
    }
  }, [currentStep])

  const progress = useMemo(() => ((stepIndex + 1) / TOTAL_STEPS) * 100, [stepIndex])

  const handleLanguageToggle = useCallback(() => {
    const newLanguage = isRussian ? LANGS.ENGLISH : LANGS.RUSSIAN
    setSelectedLanguage(newLanguage)
    dispatch(changeLanguage(newLanguage))
  }, [dispatch, isRussian])

  useEffect(() => {
  const setVH = () => {
    document.documentElement.style.setProperty("--vh", `${window.innerHeight * 0.01}px`)
  }
  setVH()
  window.addEventListener("resize", setVH)
  return () => window.removeEventListener("resize", setVH)
}, [])

  return (
    <div className='w-screen bg-rgba(197, 119, 186, 0.2) flex flex-col' style={{ height: "calc(var(--vh, 1vh) * 100)" }}>
      <div className='px-6 pt-6'>
        <div className='flex justify-between items-center'>
          <div className='flex-1 mr-6'>
            <h2 className='text-sm font-medium text-blue-500 mb-2 flex items-center space-x-2'>
              <span>
                <img src={questionMark} alt='Question mark' className='h-4 w-4 mx-1 font-extrabold' />
              </span>
              {translate('risk_assessment.questionnaire.progress')
                .replace('{current}', String(stepIndex + 1))
                .replace('{total}', String(TOTAL_STEPS))}
            </h2>
            <div className='w-full h-2 bg-[#E6F6FF] rounded-full overflow-hidden'>
              <div
                style={{ width: `${progress}%` }}
                className='h-2 bg-blue-600 rounded-full transition-all duration-500'
              />
            </div>
          </div>
          {/* Language Switch */}
          <div className='scale-75 xs:scale-85 sm:scale-90'>
            <SwitchLanguage isChecked={isRussian} onHandleChange={handleLanguageToggle} />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className='flex-1 overflow-hidden'>
        {currentStep === TutorialStep.QUESTION && (
          <TutorialQuestion onNext={() => setCurrentStep(TutorialStep.VIDEO)} translate={translate} />
        )}
        {currentStep === TutorialStep.VIDEO && (
          <VideoTutorial
            onNext={() => setCurrentStep(TutorialStep.TIMER)}
            translate={translate}
            language={selectedLanguage}
          />
        )}
        {currentStep === TutorialStep.TIMER && <Timer translate={translate} language={selectedLanguage} />}
      </div>
    </div>
  )
}

export default VitalsTutorial
